import React, { useState, useEffect, useCallback } from 'react';
import axios from '@/utils/api';
import { API } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  UserX, 
  Mail, 
  Trash2, 
  RefreshCw, 
  Settings, 
  Clock, 
  AlertTriangle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Search,
  Calendar,
  BookOpen,
  Loader2,
  Users,
  TrendingUp,
  ArrowRight,
  BarChart3,
  ChevronDown,
  Crown,
  Sparkles,
  Star,
  UserPlus,
  Copy,
  Eye,
  EyeOff,
  X,
  Pencil,
  Rocket,
  UserCheck
} from 'lucide-react';
import { format } from 'date-fns';
import OnboardingNudgeManager from './OnboardingNudgeManager';
import DuplicateAccountsManager from './DuplicateAccountsManager';

// Tier Badge Component for styled tier labels
const TierBadge = ({ tier }) => {
  const tierConfig = {
    founding_member: {
      label: 'Founding Member',
      icon: Crown,
      bgColor: 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20',
      textColor: 'text-amber-400',
      borderColor: 'border-amber-500/30',
    },
    premium: {
      label: 'Premium',
      icon: Star,
      bgColor: 'bg-gradient-to-r from-orange-500/20 to-pink-500/20',
      textColor: 'text-orange-400',
      borderColor: 'border-orange-500/30',
    },
    enterprise: {
      label: 'Enterprise',
      icon: Sparkles,
      bgColor: 'bg-gradient-to-r from-purple-500/20 to-indigo-500/20',
      textColor: 'text-purple-400',
      borderColor: 'border-purple-500/30',
    },
    free: {
      label: 'Free',
      icon: null,
      bgColor: 'bg-[#FDF2F8]/10',
      textColor: 'text-[#FDF2F8]/60',
      borderColor: 'border-transparent',
    },
  };

  const config = tierConfig[tier] || tierConfig.free;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.bgColor} ${config.textColor} ${config.borderColor}`}>
      {Icon && <Icon className="w-3 h-3" />}
      {config.label}
    </span>
  );
};

const InactiveAccountsTab = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('inactive'); // 'inactive', 'active', 'analytics'
  
  // Inactive users state
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState({
    inactive_threshold_days: 180,
    warning_email_days_before_deletion: 30,
    grace_period_days: 30,
    auto_send_reengagement: false
  });
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [thresholdDays, setThresholdDays] = useState(180);
  const [showSettings, setShowSettings] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Re-engagement step selector
  const [selectedStep, setSelectedStep] = useState(1);
  const [showStepDropdown, setShowStepDropdown] = useState(false);
  
  // Email confirmation dialog state
  const [showEmailConfirm, setShowEmailConfirm] = useState(false);
  const [emailStepToSend, setEmailStepToSend] = useState(1);
  
  // Active users state
  const [activeUsers, setActiveUsers] = useState([]);
  const [activeLoading, setActiveLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [activeTotalPages, setActiveTotalPages] = useState(1);
  const [activeTotalCount, setActiveTotalCount] = useState(0);
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [activeDaysThreshold, setActiveDaysThreshold] = useState(30);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Deletion Flow Analytics state
  const [deletionAnalytics, setDeletionAnalytics] = useState(null);
  const [deletionAnalyticsLoading, setDeletionAnalyticsLoading] = useState(false);
  
  // Deletion Warning step selector
  const [deletionStep, setDeletionStep] = useState(1);
  const [showDeletionStepDropdown, setShowDeletionStepDropdown] = useState(false);
  const [showDeletionConfirm, setShowDeletionConfirm] = useState(false);
  const [deletionStepToSend, setDeletionStepToSend] = useState(1);
  
  // B2B Cleanup state
  const [b2bCleanupLoading, setB2bCleanupLoading] = useState(false);
  const [b2bCleanupResult, setB2bCleanupResult] = useState(null);
  
  // Email Open Tracking state
  const [emailOpenTracking, setEmailOpenTracking] = useState(null);
  const [emailOpenLoading, setEmailOpenLoading] = useState(false);
  const [showEmailOpenModal, setShowEmailOpenModal] = useState(false);

  // Create User state
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    membership_tier: 'free',
    send_welcome_email: true,
    password: ''
  });
  const [createdUserResult, setCreatedUserResult] = useState(null);

  // Edit User state
  const [showEditUser, setShowEditUser] = useState(false);
  const [editUserLoading, setEditUserLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    membership_tier: 'free',
    reset_password: false,
    new_password: ''
  });

  // Select All state
  const [allUserIds, setAllUserIds] = useState([]); // All inactive user IDs for "Select All"
  const [selectAllMode, setSelectAllMode] = useState(false); // Whether we're in "all selected" mode
  const [loadingAllUsers, setLoadingAllUsers] = useState(false);

  // Fetch ALL user IDs for bulk selection (no pagination)
  const fetchAllUserIds = async () => {
    try {
      setLoadingAllUsers(true);
      const res = await axios.get(`${API}/admin/inactive-accounts?threshold_days=${thresholdDays}&page=1&limit=10000&include_scheduled=true`);
      const ids = (res.data.users || []).map(u => u.user_id);
      setAllUserIds(ids);
      return ids;
    } catch (error) {
      console.error('Error fetching all user IDs:', error);
      toast.error('Failed to fetch all users');
      return [];
    } finally {
      setLoadingAllUsers(false);
    }
  };

  const fetchInactiveUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/inactive-accounts?threshold_days=${thresholdDays}&page=${page}&limit=20&include_scheduled=true`);
      setInactiveUsers(res.data.users || []);
      setTotalPages(res.data.total_pages || 1);
      setTotalCount(res.data.total_count || 0);
    } catch (error) {
      console.error('Error fetching inactive users:', error);
      toast.error('Failed to load inactive accounts');
    } finally {
      setLoading(false);
    }
  }, [thresholdDays, page]);

  const fetchActiveUsers = useCallback(async () => {
    try {
      setActiveLoading(true);
      const params = new URLSearchParams({
        page: activePage,
        limit: 20,
        days_threshold: activeDaysThreshold
      });
      if (activeSearchQuery) params.append('search', activeSearchQuery);
      
      const res = await axios.get(`${API}/admin/active-accounts?${params}`);
      setActiveUsers(res.data.users || []);
      setActiveTotalPages(res.data.total_pages || 1);
      setActiveTotalCount(res.data.total_count || 0);
    } catch (error) {
      console.error('Error fetching active users:', error);
      toast.error('Failed to load active accounts');
    } finally {
      setActiveLoading(false);
    }
  }, [activePage, activeDaysThreshold, activeSearchQuery]);

  const fetchAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const res = await axios.get(`${API}/admin/reengagement-analytics`, {
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
      });
      setAnalytics(res.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };
  
  const fetchDeletionAnalytics = async () => {
    try {
      setDeletionAnalyticsLoading(true);
      const res = await axios.get(`${API}/admin/deletion-flow-analytics`, {
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
      });
      setDeletionAnalytics(res.data);
    } catch (error) {
      console.error('Error fetching deletion analytics:', error);
      toast.error('Failed to load deletion flow analytics');
    } finally {
      setDeletionAnalyticsLoading(false);
    }
  };
  
  // Fetch email open tracking data
  const fetchEmailOpenTracking = async () => {
    try {
      setEmailOpenLoading(true);
      const res = await axios.get(`${API}/admin/email-open-tracking`, {
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
      });
      setEmailOpenTracking(res.data);
    } catch (error) {
      console.error('Error fetching email open tracking:', error);
      toast.error('Failed to load email open tracking');
    } finally {
      setEmailOpenLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/inactive-accounts/stats`);
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/inactive-accounts/settings`);
      setSettings(res.data);
      setThresholdDays(res.data.inactive_threshold_days || 180);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchStats();
    // Also fetch active users count for the tab badge
    fetchActiveUsersCount();
  }, []);

  // Fetch just the count for the Active tab badge
  const fetchActiveUsersCount = async () => {
    try {
      const res = await axios.get(`${API}/admin/active-accounts?days_threshold=${activeDaysThreshold}&page=1&limit=1`);
      setActiveTotalCount(res.data.total_count || 0);
    } catch (error) {
      console.error('Error fetching active users count:', error);
    }
  };

  useEffect(() => {
    fetchInactiveUsers();
  }, [fetchInactiveUsers]);

  useEffect(() => {
    if (activeTab === 'active') {
      fetchActiveUsers();
    } else if (activeTab === 'analytics') {
      fetchAnalytics();
      fetchDeletionAnalytics();
    }
  }, [activeTab, fetchActiveUsers]);

  const handleSaveSettings = async () => {
    // Validate settings before saving
    if (settings.inactive_threshold_days < 1 || settings.grace_period_days < 1 || settings.warning_email_days_before_deletion < 1) {
      toast.error('All settings must be positive numbers');
      return;
    }
    try {
      setLoadingAction(true);
      await axios.put(`${API}/admin/inactive-accounts/settings`, settings);
      setThresholdDays(settings.inactive_threshold_days);
      toast.success('Settings saved successfully');
      setShowSettings(false);
      fetchInactiveUsers();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoadingAction(false);
    }
  };

  // B2B Cleanup Handler
  const handleB2BCleanup = async () => {
    try {
      setB2bCleanupLoading(true);
      setB2bCleanupResult(null);
      const res = await axios.post(`${API}/admin/cleanup-b2b-email-data`);
      setB2bCleanupResult(res.data);
      if (res.data.affected_users > 0) {
        toast.success(`Cleaned ${res.data.cleaned_records} B2B user records`);
      } else {
        toast.info('No B2B users needed cleanup');
      }
      // Refresh data
      fetchInactiveUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to clean up B2B data';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Cleanup failed');
    } finally {
      setB2bCleanupLoading(false);
    }
  };

  // Create User Handler
  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUser.email) {
      toast.error('Email is required');
      return;
    }
    
    try {
      setCreateUserLoading(true);
      const res = await axios.post(`${API}/admin/users/create`, {
        email: newUser.email,
        first_name: newUser.first_name,
        last_name: newUser.last_name,
        phone_number: newUser.phone_number || undefined,
        membership_tier: newUser.membership_tier,
        send_welcome_email: newUser.send_welcome_email,
        password: newUser.password || undefined
      });
      
      setCreatedUserResult(res.data);
      toast.success(`User created successfully with ${res.data.membership_tier} tier`);
      
      // Refresh user lists
      fetchActiveUsers();
      fetchInactiveUsers();
      
      // Reset form but keep modal open to show result
      setNewUser({
        email: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        membership_tier: 'free',
        send_welcome_email: true,
        password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreateUserLoading(false);
    }
  };

  // Open Edit User Modal
  const openEditUser = (user) => {
    setEditingUser(user);
    setEditUserForm({
      email: user.email || '',
      first_name: user.first_name || user.name?.split(' ')[0] || '',
      last_name: user.last_name || user.name?.split(' ').slice(1).join(' ') || '',
      membership_tier: user.membership_tier || 'free',
      reset_password: false,
      new_password: ''
    });
    setShowEditUser(true);
  };

  // Edit User Handler
  const handleEditUser = async (e) => {
    e.preventDefault();
    
    if (!editingUser) return;
    
    try {
      setEditUserLoading(true);
      
      const updateData = {
        email: editUserForm.email,
        first_name: editUserForm.first_name,
        last_name: editUserForm.last_name,
        membership_tier: editUserForm.membership_tier
      };
      
      if (editUserForm.reset_password && editUserForm.new_password) {
        updateData.new_password = editUserForm.new_password;
      }
      
      await axios.put(`${API}/admin/users/${editingUser.user_id}`, updateData);
      
      toast.success('User updated successfully');
      setShowEditUser(false);
      setEditingUser(null);
      
      // Refresh user lists
      fetchActiveUsers();
      fetchInactiveUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setEditUserLoading(false);
    }
  };

  const handleSendReengagement = async (step = selectedStep) => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users to send re-engagement emails');
      return;
    }

    // Show branded confirmation dialog instead of browser confirm
    setEmailStepToSend(step);
    setShowEmailConfirm(true);
    setShowStepDropdown(false);
  };

  // Actually send the emails after confirmation
  const confirmSendEmails = async () => {
    const step = emailStepToSend;
    const userCount = selectedUsers.length;

    try {
      setLoadingAction(true);
      setShowEmailConfirm(false);
      const res = await axios.post(`${API}/admin/inactive-accounts/send-reengagement`, {
        user_ids: selectedUsers,
        step: step
      });
      toast.success(`Email ${step} sent to ${userCount} user${userCount > 1 ? 's' : ''}`);
      setSelectedUsers([]);
      setSelectAllMode(false);
      fetchInactiveUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send re-engagement emails');
    } finally {
      setLoadingAction(false);
    }
  };

  // Deletion Warning Step handlers
  const handleDeletionStepSelect = (step) => {
    setDeletionStepToSend(step);
    setShowDeletionConfirm(true);
    setShowDeletionStepDropdown(false);
  };

  // Actually send deletion warning emails after confirmation
  const confirmSendDeletionWarning = async () => {
    const step = deletionStepToSend;
    const userCount = selectedUsers.length;

    try {
      setLoadingAction(true);
      setShowDeletionConfirm(false);
      const res = await axios.post(`${API}/admin/inactive-accounts/send-deletion-warning`, {
        user_ids: selectedUsers,
        step: step
      });
      toast.success(`Deletion Warning (Step ${step}) sent to ${res.data.sent_count} user(s)${res.data.newly_scheduled > 0 ? `. ${res.data.newly_scheduled} newly scheduled for deletion.` : ''}`);
      setSelectedUsers([]);
      setSelectAllMode(false);
      fetchInactiveUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send deletion warning emails');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleScheduleDeletion = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users to schedule for deletion');
      return;
    }

    if (!confirm(`Are you sure you want to schedule ${selectedUsers.length} account(s) for deletion? Users will receive a warning email and have ${settings.grace_period_days} days to log back in.`)) {
      return;
    }

    try {
      setLoadingAction(true);
      const res = await axios.post(`${API}/admin/inactive-accounts/schedule-deletion`, {
        user_ids: selectedUsers,
        send_notification: true
      });
      toast.success(res.data.message);
      setSelectedUsers([]);
      fetchInactiveUsers();
      fetchStats();
    } catch (error) {
      toast.error('Failed to schedule deletion');
    } finally {
      setLoadingAction(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users to delete');
      return;
    }

    if (!confirm(`⚠️ PERMANENT DELETION\n\nAre you absolutely sure you want to permanently delete ${selectedUsers.length} account(s)?\n\nThis will delete ALL their data including journal entries, mood history, and cannot be undone!`)) {
      return;
    }

    try {
      setLoadingAction(true);
      const res = await axios.post(`${API}/admin/inactive-accounts/permanent-delete`, {
        user_ids: selectedUsers
      });
      toast.success(res.data.message);
      setSelectedUsers([]);
      fetchInactiveUsers();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete accounts');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleCancelDeletion = async (userId) => {
    try {
      await axios.post(`${API}/admin/inactive-accounts/cancel-deletion/${userId}`);
      toast.success('Scheduled deletion cancelled');
      fetchInactiveUsers();
      fetchStats();
    } catch (error) {
      toast.error('Failed to cancel deletion');
    }
  };

  const toggleSelectUser = (userId) => {
    setSelectAllMode(false); // Clear select-all mode when individual selection changes
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Filter users based on search query - must be defined before toggleSelectAll
  const filteredUsers = inactiveUsers.filter(user => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.email?.toLowerCase().includes(query) ||
      user.first_name?.toLowerCase().includes(query) ||
      user.name?.toLowerCase().includes(query)
    );
  });

  // Toggle select all on current page
  const toggleSelectAllPage = () => {
    if (selectedUsers.length === filteredUsers.length && filteredUsers.length > 0) {
      setSelectedUsers([]);
      setSelectAllMode(false);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.user_id));
      setSelectAllMode(false);
    }
  };

  // Select ALL users across ALL pages
  const handleSelectAllUsers = async () => {
    if (selectAllMode) {
      // Deselect all
      setSelectedUsers([]);
      setSelectAllMode(false);
    } else {
      // Select all users
      const ids = allUserIds.length > 0 ? allUserIds : await fetchAllUserIds();
      setSelectedUsers(ids);
      setSelectAllMode(true);
      toast.success(`Selected all ${ids.length} inactive users`);
    }
  };

  return (
    <div className="space-y-6 overflow-hidden" data-testid="inactive-accounts-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2" data-testid="inactive-accounts-header">
          <Users className="w-5 h-5 text-[#FF7901]" />
          User Engagement Management
        </h2>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => {
              setShowCreateUser(true);
              setCreatedUserResult(null);
            }}
            className="bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white hover:opacity-90"
            data-testid="create-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Member
          </Button>
          <Button
            onClick={() => setShowSettings(!showSettings)}
            variant="outline"
            className="border-[#df1283]/30 text-white hover:bg-[#df1283]/10"
            data-testid="inactive-accounts-settings-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          <Button
            onClick={() => { 
              if (activeTab === 'inactive') {
                fetchInactiveUsers(); 
                fetchStats();
              } else if (activeTab === 'active') {
                fetchActiveUsers();
              } else {
                fetchAnalytics();
              }
            }}
            variant="outline"
            className="border-[#0FFFE2]/30 text-[#0FFFE2] hover:bg-[#0FFFE2]/10"
            data-testid="inactive-accounts-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 sm:gap-2 border-b border-[#4A0A66]/50 pb-2 overflow-x-auto" data-testid="engagement-tabs">
        <button
          onClick={() => setActiveTab('inactive')}
          data-testid="tab-inactive"
          className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-t-lg transition-colors flex items-center gap-2 text-sm whitespace-nowrap ${
            activeTab === 'inactive'
              ? 'bg-[#df1283]/20 text-[#df1283] border-b-2 border-[#df1283]'
              : 'text-[#FDF2F8]/50 hover:text-white hover:bg-[#4A0A66]/30'
          }`}
        >
          <UserX className="w-4 h-4" />
          Inactive ({stats?.total_inactive || 0})
        </button>
        <button
          onClick={() => setActiveTab('active')}
          data-testid="tab-active"
          className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-t-lg transition-colors flex items-center gap-2 text-sm whitespace-nowrap ${
            activeTab === 'active'
              ? 'bg-[#0FFFE2]/20 text-[#0FFFE2] border-b-2 border-[#0FFFE2]'
              : 'text-[#FDF2F8]/50 hover:text-white hover:bg-[#4A0A66]/30'
          }`}
        >
          <CheckCircle className="w-4 h-4" />
          Active ({activeTotalCount})
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          data-testid="tab-analytics"
          className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-t-lg transition-colors flex items-center gap-2 text-sm whitespace-nowrap ${
            activeTab === 'analytics'
              ? 'bg-[#FF7901]/20 text-[#FF7901] border-b-2 border-[#FF7901]'
              : 'text-[#FDF2F8]/50 hover:text-white hover:bg-[#4A0A66]/30'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          Analytics
        </button>
        <button
          onClick={() => setActiveTab('onboarding-nudge')}
          data-testid="tab-onboarding-nudge"
          className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-t-lg transition-colors flex items-center gap-2 text-sm whitespace-nowrap ${
            activeTab === 'onboarding-nudge'
              ? 'bg-[#df1283]/20 text-[#df1283] border-b-2 border-[#df1283]'
              : 'text-[#FDF2F8]/50 hover:text-white hover:bg-[#4A0A66]/30'
          }`}
        >
          <Rocket className="w-4 h-4" />
          Onboarding Nudge
        </button>
        <button
          onClick={() => setActiveTab('duplicate-accounts')}
          data-testid="tab-duplicate-accounts"
          className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-t-lg transition-colors flex items-center gap-2 text-sm whitespace-nowrap ${
            activeTab === 'duplicate-accounts'
              ? 'bg-amber-500/20 text-amber-400 border-b-2 border-amber-500'
              : 'text-[#FDF2F8]/50 hover:text-white hover:bg-[#4A0A66]/30'
          }`}
        >
          <UserCheck className="w-4 h-4" />
          Duplicate Accounts
        </button>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="bg-[#2D063E]/50 rounded-xl p-6 border border-[#df1283]/20">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Inactive Account Settings
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-[#FDF2F8]/70 text-sm block mb-2">
                Inactive Threshold (days)
              </label>
              <Input
                type="number"
                value={settings.inactive_threshold_days}
                onChange={(e) => setSettings({...settings, inactive_threshold_days: parseInt(e.target.value) || 180})}
                className="bg-[#1a0527] border-[#df1283]/30 text-white"
              />
              <p className="text-xs text-[#FDF2F8]/50 mt-1">Users inactive for this many days appear in the list</p>
            </div>
            <div>
              <label className="text-[#FDF2F8]/70 text-sm block mb-2">
                Grace Period Before Deletion (days)
              </label>
              <Input
                type="number"
                value={settings.grace_period_days}
                onChange={(e) => setSettings({...settings, grace_period_days: parseInt(e.target.value) || 30})}
                className="bg-[#1a0527] border-[#df1283]/30 text-white"
              />
              <p className="text-xs text-[#FDF2F8]/50 mt-1">Time users have to log back in before deletion</p>
            </div>
            <div>
              <label className="text-[#FDF2F8]/70 text-sm block mb-2">
                Warning Email Days Before Deletion
              </label>
              <Input
                type="number"
                value={settings.warning_email_days_before_deletion}
                onChange={(e) => setSettings({...settings, warning_email_days_before_deletion: parseInt(e.target.value) || 30})}
                className="bg-[#1a0527] border-[#df1283]/30 text-white"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              onClick={handleSaveSettings}
              disabled={loadingAction}
              className="bg-gradient-to-r from-[#df1283] to-[#FF7901]"
            >
              {loadingAction ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Settings
            </Button>
          </div>
          
          {/* B2B Cleanup Section */}
          <div className="mt-6 pt-6 border-t border-[#4A0A66]">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              B2B User Cleanup
            </h4>
            <p className="text-sm text-white/60 mb-4">
              Remove any email sequence data (nudge, re-engagement) from B2B users. 
              B2B users belong to their organization portal and should not receive main site emails.
            </p>
            <Button
              onClick={handleB2BCleanup}
              disabled={b2bCleanupLoading}
              variant="outline"
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
              data-testid="b2b-cleanup-btn"
            >
              {b2bCleanupLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Clean Up B2B Email Data
            </Button>
            {b2bCleanupResult && (
              <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-sm">
                <p className="text-green-400 font-medium">{b2bCleanupResult.message}</p>
                <ul className="text-white/70 mt-2 space-y-1">
                  <li>• Total B2B users: {b2bCleanupResult.total_b2b_users}</li>
                  <li>• Affected users: {b2bCleanupResult.affected_users}</li>
                  <li>• Records cleaned: {b2bCleanupResult.cleaned_records}</li>
                  <li>• Email logs deleted: {b2bCleanupResult.deleted_email_logs}</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* INACTIVE TAB CONTENT */}
      {activeTab === 'inactive' && (
        <>
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="inactive-accounts-stats">
              <div className="bg-[#2D063E]/50 rounded-xl p-4 border border-[#df1283]/20" data-testid="stat-inactive-30-days">
                <div className="flex items-center gap-2 text-[#FDF2F8]/70 text-sm mb-1">
                  <Clock className="w-4 h-4" />
                  Inactive 30+ days
                </div>
                <div className="text-2xl font-bold text-white">{stats.inactive_counts?.['30_days'] || 0}</div>
              </div>
              <div className="bg-[#2D063E]/50 rounded-xl p-4 border border-[#FF7901]/20" data-testid="stat-inactive-90-days">
                <div className="flex items-center gap-2 text-[#FDF2F8]/70 text-sm mb-1">
                  <Clock className="w-4 h-4" />
                  Inactive 90+ days
                </div>
                <div className="text-2xl font-bold text-[#FF7901]">{stats.inactive_counts?.['90_days'] || 0}</div>
              </div>
              <div className="bg-[#2D063E]/50 rounded-xl p-4 border border-red-500/20" data-testid="stat-inactive-180-days">
                <div className="flex items-center gap-2 text-[#FDF2F8]/70 text-sm mb-1">
                  <AlertTriangle className="w-4 h-4" />
                  Inactive 180+ days
                </div>
                <div className="text-2xl font-bold text-red-400">{stats.inactive_counts?.['180_days'] || 0}</div>
              </div>
              <div className="bg-[#2D063E]/50 rounded-xl p-4 border border-purple-500/20" data-testid="stat-inactive-365-days">
                <div className="flex items-center gap-2 text-[#FDF2F8]/70 text-sm mb-1">
                  <AlertTriangle className="w-4 h-4" />
                  Inactive 365+ days
                </div>
                <div className="text-2xl font-bold text-purple-400">{stats.inactive_counts?.['365_days'] || 0}</div>
              </div>
              <div className="bg-[#2D063E]/50 rounded-xl p-4 border border-[#0FFFE2]/20" data-testid="stat-scheduled-deletion">
                <div className="flex items-center gap-2 text-[#FDF2F8]/70 text-sm mb-1">
                  <Trash2 className="w-4 h-4" />
                  Scheduled Deletion
                </div>
                <div className="text-2xl font-bold text-[#0FFFE2]">{stats.scheduled_for_deletion || 0}</div>
              </div>
            </div>
          )}

      {/* Threshold Selector */}
      <div className="flex items-center gap-4 flex-wrap">
        <span className="text-[#FDF2F8]/70 text-sm">Show users inactive for:</span>
        {[30, 90, 180, 365].map(days => (
          <Button
            key={days}
            onClick={() => { setThresholdDays(days); setPage(1); }}
            variant={thresholdDays === days ? 'default' : 'outline'}
            size="sm"
            className={thresholdDays === days 
              ? 'bg-gradient-to-r from-[#df1283] to-[#FF7901]' 
              : 'border-[#df1283]/30 text-white hover:bg-[#df1283]/10'
            }
          >
            {days}+ days
          </Button>
        ))}
      </div>

      {/* Search and Actions */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FDF2F8]/50" />
          <Input
            placeholder="Search by email or name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#1a0527] border-[#df1283]/30 text-white"
          />
        </div>
        
        {/* Select All Button - Always visible when there are inactive users */}
        {totalCount > 0 && (
          <Button
            onClick={handleSelectAllUsers}
            disabled={loadingAllUsers}
            variant="outline"
            size="sm"
            className={selectAllMode 
              ? "bg-[#0FFFE2]/20 border-[#0FFFE2] text-[#0FFFE2]"
              : "border-[#df1283]/30 text-white hover:bg-[#df1283]/10"
            }
            data-testid="select-all-users-btn"
          >
            {loadingAllUsers ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Users className="w-4 h-4 mr-1" />
            )}
            {selectAllMode ? `Deselect All (${selectedUsers.length})` : `Select All ${totalCount} Users`}
          </Button>
        )}
        
        {selectedUsers.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[#0FFFE2] text-sm font-medium">
              {selectedUsers.length} of {totalCount} selected
              {selectAllMode && <span className="ml-1">(All Pages)</span>}
            </span>
            
            {/* Re-engagement Step Dropdown */}
            <div className="relative">
              <Button
                onClick={() => setShowStepDropdown(!showStepDropdown)}
                disabled={loadingAction}
                size="sm"
                className="bg-[#0FFFE2] text-black hover:bg-[#0FFFE2]/80"
                data-testid="send-reengagement-btn"
              >
                <Mail className="w-4 h-4 mr-1" />
                Send Email (Step {selectedStep})
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
              {showStepDropdown && (
                <div className="absolute top-full left-0 mt-1 bg-[#2D063E] border border-[#0FFFE2]/30 rounded-lg shadow-lg z-10 min-w-[200px]">
                  <div className="p-2 text-xs text-[#FDF2F8]/50 border-b border-[#4A0A66]">
                    Select Email Step
                  </div>
                  {[1, 2, 3].map((step) => (
                    <button
                      key={step}
                      onClick={() => {
                        setSelectedStep(step);
                        handleSendReengagement(step);
                      }}
                      className="w-full px-4 py-2 text-left text-white hover:bg-[#0FFFE2]/20 flex items-center gap-2"
                    >
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        step === 1 ? 'bg-[#0FFFE2]/30 text-[#0FFFE2]' :
                        step === 2 ? 'bg-[#FF7901]/30 text-[#FF7901]' :
                        'bg-[#df1283]/30 text-[#df1283]'
                      }`}>
                        {step}
                      </span>
                      <div>
                        <div className="text-sm">
                          {step === 1 ? 'Email 1 - "We Miss You"' :
                           step === 2 ? 'Email 2 - "Still Thinking of You"' :
                           'Email 3 - "One Last Note"'}
                        </div>
                        <div className="text-xs text-[#FDF2F8]/50">
                          {step === 1 ? 'Initial outreach' :
                           step === 2 ? 'Gentle follow-up' :
                           'Last attempt before giving up'}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Deletion Warning Step Dropdown */}
            <div className="relative">
              <Button
                onClick={() => setShowDeletionStepDropdown(!showDeletionStepDropdown)}
                disabled={loadingAction}
                size="sm"
                className="bg-[#FF7901] hover:bg-[#FF7901]/80 text-white"
                data-testid="send-deletion-warning-btn"
              >
                <AlertTriangle className="w-4 h-4 mr-1" />
                Deletion Warning
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
              {showDeletionStepDropdown && (
                <div className="absolute top-full left-0 mt-1 bg-[#2D063E] border border-[#FF7901]/30 rounded-lg shadow-lg z-10 min-w-[250px]">
                  <div className="p-2 text-xs text-[#FDF2F8]/50 border-b border-[#4A0A66]">
                    Select Deletion Warning Email
                  </div>
                  {[1, 2, 3].map((step) => (
                    <button
                      key={step}
                      onClick={() => handleDeletionStepSelect(step)}
                      className="w-full px-4 py-2 text-left text-white hover:bg-[#FF7901]/20 flex items-center gap-2"
                    >
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        step === 1 ? 'bg-[#FF7901]/30 text-[#FF7901]' :
                        step === 2 ? 'bg-orange-500/30 text-orange-400' :
                        'bg-red-500/30 text-red-400'
                      }`}>
                        {step}
                      </span>
                      <div>
                        <div className="text-sm">
                          {step === 1 ? 'Warning 1 - "Account Scheduled"' :
                           step === 2 ? 'Warning 2 - "Time Running Out"' :
                           'Warning 3 - "FINAL NOTICE"'}
                        </div>
                        <div className="text-xs text-[#FDF2F8]/50">
                          {step === 1 ? 'Initial deletion notice' :
                           step === 2 ? 'Halfway reminder' :
                           'Last chance before deletion'}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <Button
              onClick={handlePermanentDelete}
              disabled={loadingAction}
              size="sm"
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              data-testid="permanent-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete Now
            </Button>
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-[#2D063E]/30 rounded-xl border border-[#df1283]/20 overflow-hidden" data-testid="inactive-accounts-table-container">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="inactive-accounts-table">
            <thead className="bg-[#1a0527]">
              <tr>
                <th className="p-3 text-left">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectAllMode || (selectedUsers.length === filteredUsers.length && filteredUsers.length > 0)}
                      onChange={toggleSelectAllPage}
                      className="rounded border-[#df1283]/30"
                      title="Select all on this page"
                    />
                    <span className="text-[#FDF2F8]/40 text-xs">Page</span>
                  </div>
                </th>
                <th className="p-3 text-left text-[#FDF2F8]/70 text-sm font-medium">User</th>
                <th className="p-3 text-left text-[#FDF2F8]/70 text-sm font-medium">Last Login</th>
                <th className="p-3 text-left text-[#FDF2F8]/70 text-sm font-medium">Days Inactive</th>
                <th className="p-3 text-left text-[#FDF2F8]/70 text-sm font-medium">Entries</th>
                <th className="p-3 text-left text-[#FDF2F8]/70 text-sm font-medium">Re-engage Step</th>
                <th className="p-3 text-left text-[#FDF2F8]/70 text-sm font-medium">Status</th>
                <th className="p-3 text-left text-[#FDF2F8]/70 text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-[#FDF2F8]/50">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading inactive accounts...
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-[#FDF2F8]/50">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-[#0FFFE2]" />
                    No inactive accounts found for {thresholdDays}+ days threshold
                  </td>
                </tr>
              ) : (
                filteredUsers.map(user => (
                  <tr key={user.user_id} className="border-t border-[#df1283]/10 hover:bg-[#df1283]/5">
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.user_id)}
                        onChange={() => toggleSelectUser(user.user_id)}
                        className="rounded border-[#df1283]/30"
                      />
                    </td>
                    <td className="p-3">
                      <div className="text-white font-medium">{user.first_name || user.name || 'Unnamed'}</div>
                      <div className="text-[#FDF2F8]/50 text-sm">{user.email}</div>
                    </td>
                    <td className="p-3 text-[#FDF2F8]/70 text-sm">
                      {user.last_login ? (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {format(new Date(user.last_login), 'MMM d, yyyy')}
                        </div>
                      ) : (
                        <span className="text-[#FDF2F8]/40">Never logged in</span>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`font-medium ${
                        user.days_inactive > 365 ? 'text-red-400' :
                        user.days_inactive > 180 ? 'text-[#FF7901]' :
                        user.days_inactive > 90 ? 'text-yellow-400' :
                        'text-[#FDF2F8]/70'
                      }`}>
                        {user.days_inactive || '—'} days
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1 text-[#FDF2F8]/70">
                        <BookOpen className="w-3 h-3" />
                        {user.entry_count || 0}
                      </div>
                    </td>
                    <td className="p-3">
                      {user.reengagement_step ? (
                        <div className="flex items-center gap-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            user.reengagement_step === 1 ? 'bg-[#0FFFE2]/20 text-[#0FFFE2]' :
                            user.reengagement_step === 2 ? 'bg-[#FF7901]/20 text-[#FF7901]' :
                            'bg-[#df1283]/20 text-[#df1283]'
                          }`}>
                            Step {user.reengagement_step}/3
                          </span>
                          <ArrowRight className="w-3 h-3 text-[#FDF2F8]/30" />
                          <span className="text-xs text-[#FDF2F8]/50">
                            {user.reengagement_step < 3 ? `Send ${user.reengagement_step + 1}` : 'Done'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-[#FDF2F8]/40">Not started</span>
                      )}
                    </td>
                    <td className="p-3">
                      {user.scheduled_for_deletion ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/20 text-red-400 text-xs">
                          <AlertTriangle className="w-3 h-3" />
                          Scheduled for deletion
                        </span>
                      ) : user.reengagement_email_sent ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#0FFFE2]/20 text-[#0FFFE2] text-xs">
                          <Mail className="w-3 h-3" />
                          Re-engagement sent
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FF7901]/20 text-[#FF7901] text-xs">
                          <Clock className="w-3 h-3" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      {user.scheduled_for_deletion && (
                        <Button
                          onClick={() => handleCancelDeletion(user.user_id)}
                          size="sm"
                          variant="outline"
                          className="border-[#0FFFE2]/30 text-[#0FFFE2] hover:bg-[#0FFFE2]/10 text-xs"
                        >
                          Cancel Deletion
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-[#df1283]/10">
            <span className="text-[#FDF2F8]/50 text-sm">
              Showing {filteredUsers.length} of {totalCount} inactive accounts
            </span>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                size="sm"
                variant="outline"
                className="border-[#df1283]/30 text-white"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-white text-sm">
                Page {page} of {totalPages}
              </span>
              <Button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                size="sm"
                variant="outline"
                className="border-[#df1283]/30 text-white"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Help Text */}
      <div className="bg-[#2D063E]/30 rounded-xl p-4 border border-[#df1283]/10">
        <h4 className="text-white font-medium mb-2">How it works:</h4>
        <ol className="text-[#FDF2F8]/70 text-sm space-y-2 list-decimal list-inside">
          <li><strong>3-Step Re-engagement:</strong> Send up to 3 progressive emails to bring users back (select step from dropdown)</li>
          <li><strong>Schedule Deletion:</strong> Mark accounts for deletion after a grace period ({settings.grace_period_days} days). Users receive a warning email.</li>
          <li><strong>Auto-Cancel:</strong> If a user logs in during the grace period, their scheduled deletion is automatically cancelled</li>
          <li><strong>Permanent Delete:</strong> Immediately and permanently delete accounts and all their data (cannot be undone)</li>
        </ol>
      </div>
        </>
      )}

      {/* ACTIVE TAB CONTENT */}
      {activeTab === 'active' && (
        <div className="space-y-6">
          {/* Active Users Filter */}
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-[#FDF2F8]/70 text-sm">Show users active in last:</span>
            {[7, 14, 30, 60, 90].map(days => (
              <Button
                key={days}
                onClick={() => { setActiveDaysThreshold(days); setActivePage(1); }}
                size="sm"
                variant={activeDaysThreshold === days ? "default" : "outline"}
                className={activeDaysThreshold === days 
                  ? "bg-[#0FFFE2] text-black" 
                  : "border-[#0FFFE2]/30 text-white hover:bg-[#0FFFE2]/10"}
              >
                {days} days
              </Button>
            ))}
            <div className="flex-1 max-w-xs ml-auto">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#FDF2F8]/30" />
                <Input
                  placeholder="Search active users..."
                  value={activeSearchQuery}
                  onChange={(e) => setActiveSearchQuery(e.target.value)}
                  className="pl-9 bg-[#1a0527] border-[#0FFFE2]/30 text-white"
                />
              </div>
            </div>
          </div>

          {/* Active Users Table */}
          <div className="bg-[#2D063E]/30 rounded-xl border border-[#0FFFE2]/20 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1a0527]">
                  <tr>
                    <th className="p-3 text-left text-[#FDF2F8]/70 text-sm font-medium">User</th>
                    <th className="p-3 text-left text-[#FDF2F8]/70 text-sm font-medium">Last Login</th>
                    <th className="p-3 text-left text-[#FDF2F8]/70 text-sm font-medium">Days Active</th>
                    <th className="p-3 text-left text-[#FDF2F8]/70 text-sm font-medium">Total Entries</th>
                    <th className="p-3 text-left text-[#FDF2F8]/70 text-sm font-medium">This Week</th>
                    <th className="p-3 text-left text-[#FDF2F8]/70 text-sm font-medium">This Month</th>
                    <th className="p-3 text-left text-[#FDF2F8]/70 text-sm font-medium">Tier</th>
                    <th className="p-3 text-left text-[#FDF2F8]/70 text-sm font-medium w-16">Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {activeLoading ? (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-[#FDF2F8]/50">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                        Loading active users...
                      </td>
                    </tr>
                  ) : activeUsers.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-[#FDF2F8]/50">
                        No active users found
                      </td>
                    </tr>
                  ) : (
                    activeUsers.map(user => (
                      <tr key={user.user_id} className="border-t border-[#0FFFE2]/10 hover:bg-[#0FFFE2]/5">
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            {user.picture && (
                              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                            )}
                            <div>
                              <div className="text-white font-medium">{user.first_name || user.name || 'Unnamed'}</div>
                              <div className="text-[#FDF2F8]/50 text-sm">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-3 text-[#FDF2F8]/70 text-sm">
                          {user.last_login ? (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {format(new Date(user.last_login), 'MMM d, yyyy')}
                            </div>
                          ) : (
                            <span className="text-[#FDF2F8]/40">—</span>
                          )}
                        </td>
                        <td className="p-3">
                          <span className="text-[#0FFFE2] font-medium">
                            {user.is_new_account 
                              ? 'New account' 
                              : user.days_since_login === 0 
                                ? 'Today' 
                                : user.days_since_login === 1 
                                  ? 'Yesterday' 
                                  : `${user.days_since_login} days ago`}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1 text-white font-medium">
                            <BookOpen className="w-3 h-3 text-[#0FFFE2]" />
                            {user.entry_count || 0}
                          </div>
                        </td>
                        <td className="p-3 text-[#FDF2F8]/70">{user.entries_this_week || 0}</td>
                        <td className="p-3 text-[#FDF2F8]/70">{user.entries_this_month || 0}</td>
                        <td className="p-3">
                          <TierBadge tier={user.membership_tier} />
                        </td>
                        <td className="p-3">
                          <button
                            onClick={() => openEditUser(user)}
                            className="p-1.5 rounded-lg text-[#FDF2F8]/50 hover:text-white hover:bg-white/10 transition-colors"
                            title="Edit user"
                            data-testid={`edit-user-${user.user_id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {activeTotalPages > 1 && (
              <div className="p-4 border-t border-[#0FFFE2]/10 flex items-center justify-between">
                <span className="text-[#FDF2F8]/50 text-sm">
                  Showing {(activePage - 1) * 20 + 1} - {Math.min(activePage * 20, activeTotalCount)} of {activeTotalCount}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => setActivePage(p => Math.max(1, p - 1))}
                    disabled={activePage === 1}
                    size="sm"
                    variant="outline"
                    className="border-[#0FFFE2]/30 text-white"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-white text-sm">
                    Page {activePage} of {activeTotalPages}
                  </span>
                  <Button
                    onClick={() => setActivePage(p => Math.min(activeTotalPages, p + 1))}
                    disabled={activePage === activeTotalPages}
                    size="sm"
                    variant="outline"
                    className="border-[#0FFFE2]/30 text-white"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ANALYTICS TAB CONTENT */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          {analyticsLoading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#FF7901]" />
              <p className="text-[#FDF2F8]/50">Loading analytics...</p>
            </div>
          ) : analytics ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-[#2D063E]/50 rounded-xl p-6 border border-[#0FFFE2]/20">
                  <div className="flex items-center gap-2 text-[#FDF2F8]/70 text-sm mb-2">
                    <TrendingUp className="w-4 h-4 text-[#0FFFE2]" />
                    Total Recovered
                  </div>
                  <div className="text-3xl font-bold text-[#0FFFE2]">{analytics.summary?.total_recovered || 0}</div>
                  <div className="text-xs text-[#FDF2F8]/50 mt-1">Users brought back</div>
                </div>
                <div className="bg-[#2D063E]/50 rounded-xl p-6 border border-[#FF7901]/20">
                  <div className="flex items-center gap-2 text-[#FDF2F8]/70 text-sm mb-2">
                    <Mail className="w-4 h-4 text-[#FF7901]" />
                    Emails Sent
                  </div>
                  <div className="text-3xl font-bold text-[#FF7901]">{analytics.summary?.total_emails_sent || 0}</div>
                  <div className="text-xs text-[#FDF2F8]/50 mt-1">Re-engagement campaigns</div>
                </div>
                <div className="bg-[#2D063E]/50 rounded-xl p-6 border border-[#df1283]/20">
                  <div className="flex items-center gap-2 text-[#FDF2F8]/70 text-sm mb-2">
                    <BarChart3 className="w-4 h-4 text-[#df1283]" />
                    Success Rate
                  </div>
                  <div className="text-3xl font-bold text-[#df1283]">{analytics.summary?.overall_success_rate || 0}%</div>
                  <div className="text-xs text-[#FDF2F8]/50 mt-1">Overall conversion</div>
                </div>
                <div className="bg-[#2D063E]/50 rounded-xl p-6 border border-purple-500/20">
                  <div className="flex items-center gap-2 text-[#FDF2F8]/70 text-sm mb-2">
                    <Calendar className="w-4 h-4 text-purple-400" />
                    Recent (30d)
                  </div>
                  <div className="text-3xl font-bold text-purple-400">{analytics.summary?.recent_recoveries_30d || 0}</div>
                  <div className="text-xs text-[#FDF2F8]/50 mt-1">Recovered this month</div>
                </div>
              </div>

              {/* By Step Breakdown */}
              <div className="bg-[#2D063E]/50 rounded-xl p-6 border border-[#4A0A66]">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#FF7901]" />
                  Performance by Email Step
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[1, 2, 3].map(step => {
                    const stepData = analytics.by_step?.[`step${step}`] || {};
                    return (
                      <div key={step} className={`bg-[#1a0527] rounded-lg p-4 border ${
                        step === 1 ? 'border-[#0FFFE2]/30' :
                        step === 2 ? 'border-[#FF7901]/30' :
                        'border-[#df1283]/30'
                      }`}>
                        <div className="flex items-center gap-2 mb-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            step === 1 ? 'bg-[#0FFFE2]/20 text-[#0FFFE2]' :
                            step === 2 ? 'bg-[#FF7901]/20 text-[#FF7901]' :
                            'bg-[#df1283]/20 text-[#df1283]'
                          }`}>
                            {step}
                          </span>
                          <div>
                            <div className="text-white font-medium">Step {step}</div>
                            <div className="text-xs text-[#FDF2F8]/50">
                              {step === 1 ? '"We Miss You"' :
                               step === 2 ? '"Still Thinking of You"' :
                               '"One Last Note"'}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <div className="text-[#FDF2F8]/50">Sent</div>
                            <div className="text-white font-medium">{stepData.sent || 0}</div>
                          </div>
                          <div>
                            <div className="text-[#FDF2F8]/50">Recovered</div>
                            <div className="text-[#0FFFE2] font-medium">{stepData.recovered || 0}</div>
                          </div>
                          <div>
                            <div className="text-[#FDF2F8]/50">Pending</div>
                            <div className="text-[#FF7901] font-medium">{stepData.pending || 0}</div>
                          </div>
                          <div>
                            <div className="text-[#FDF2F8]/50">Success</div>
                            <div className="text-[#df1283] font-medium">{stepData.success_rate || 0}%</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recent Recoveries */}
              {analytics.recent_recoveries?.length > 0 && (
                <div className="bg-[#2D063E]/50 rounded-xl p-6 border border-[#0FFFE2]/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#0FFFE2]" />
                    Recent Recoveries
                  </h3>
                  <div className="space-y-3">
                    {analytics.recent_recoveries.map((user) => (
                      <div key={user.user_id || user.email} className="flex items-center justify-between p-3 bg-[#1a0527] rounded-lg">
                        <div>
                          <div className="text-white font-medium">{user.name || user.first_name || user.email}</div>
                          <div className="text-[#FDF2F8]/50 text-sm">{user.email}</div>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            user.recovered_from_step === 1 ? 'bg-[#0FFFE2]/20 text-[#0FFFE2]' :
                            user.recovered_from_step === 2 ? 'bg-[#FF7901]/20 text-[#FF7901]' :
                            'bg-[#df1283]/20 text-[#df1283]'
                          }`}>
                            From Step {user.recovered_from_step}
                          </span>
                          <div className="text-xs text-[#FDF2F8]/40 mt-1">
                            {user.recovered_at && format(new Date(user.recovered_at), 'MMM d, yyyy')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deletion Flow Analytics Section */}
              <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] rounded-xl p-6 border border-[#FF7901]/30">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-[#FF7901]" />
                    Deletion Warning Flow Analytics
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchDeletionAnalytics}
                    className="border-[#FF7901]/30 text-[#FF7901] hover:bg-[#FF7901]/10"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${deletionAnalyticsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>

                {deletionAnalyticsLoading ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-[#FF7901]" />
                    <p className="text-[#FDF2F8]/50 text-sm">Loading deletion analytics...</p>
                  </div>
                ) : deletionAnalytics ? (
                  <>
                    {/* Deletion Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-[#1a0527] rounded-xl p-4 border border-[#FF7901]/20">
                        <div className="flex items-center gap-2 text-[#FDF2F8]/70 text-xs mb-1">
                          <AlertTriangle className="w-3 h-3 text-[#FF7901]" />
                          In Deletion Flow
                        </div>
                        <div className="text-2xl font-bold text-[#FF7901]">{deletionAnalytics.summary?.total_in_deletion_flow || 0}</div>
                      </div>
                      <div className="bg-[#1a0527] rounded-xl p-4 border border-[#0FFFE2]/20">
                        <div className="flex items-center gap-2 text-[#FDF2F8]/70 text-xs mb-1">
                          <CheckCircle className="w-3 h-3 text-[#0FFFE2]" />
                          Total Recovered
                        </div>
                        <div className="text-2xl font-bold text-[#0FFFE2]">{deletionAnalytics.summary?.total_recovered || 0}</div>
                      </div>
                      <div className="bg-[#1a0527] rounded-xl p-4 border border-[#df1283]/20">
                        <div className="flex items-center gap-2 text-[#FDF2F8]/70 text-xs mb-1">
                          <BarChart3 className="w-3 h-3 text-[#df1283]" />
                          Success Rate
                        </div>
                        <div className="text-2xl font-bold text-[#df1283]">{deletionAnalytics.summary?.overall_success_rate || 0}%</div>
                      </div>
                      <div className="bg-[#1a0527] rounded-xl p-4 border border-red-500/20">
                        <div className="flex items-center gap-2 text-[#FDF2F8]/70 text-xs mb-1">
                          <Trash2 className="w-3 h-3 text-red-400" />
                          Past Due
                        </div>
                        <div className="text-2xl font-bold text-red-400">{deletionAnalytics.summary?.past_due_for_deletion || 0}</div>
                      </div>
                    </div>

                    {/* Deletion By Step Breakdown */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      {[1, 2, 3].map(step => {
                        const stepData = deletionAnalytics.by_step?.[`step${step}`] || {};
                        return (
                          <div key={step} className={`bg-[#1a0527] rounded-lg p-4 border ${
                            step === 1 ? 'border-[#FF7901]/30' :
                            step === 2 ? 'border-orange-500/30' :
                            'border-red-500/30'
                          }`}>
                            <div className="flex items-center gap-2 mb-3">
                              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                                step === 1 ? 'bg-[#FF7901]/20 text-[#FF7901]' :
                                step === 2 ? 'bg-orange-500/20 text-orange-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                {step}
                              </span>
                              <div>
                                <div className="text-white text-sm font-medium">Warning {step}</div>
                                <div className="text-xs text-[#FDF2F8]/40">
                                  {step === 1 ? 'Initial notice' :
                                   step === 2 ? 'Halfway reminder' :
                                   'Final warning'}
                                </div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <div className="text-[#FDF2F8]/40">Sent</div>
                                <div className="text-white font-medium">{stepData.sent || 0}</div>
                              </div>
                              <div>
                                <div className="text-[#FDF2F8]/40">Recovered</div>
                                <div className="text-[#0FFFE2] font-medium">{stepData.recovered || 0}</div>
                              </div>
                              <div>
                                <div className="text-[#FDF2F8]/40">Pending</div>
                                <div className="text-[#FF7901] font-medium">{stepData.pending || 0}</div>
                              </div>
                              <div>
                                <div className="text-[#FDF2F8]/40">Success</div>
                                <div className="text-[#df1283] font-medium">{stepData.success_rate || 0}%</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Recent Deletion Flow Recoveries */}
                    {deletionAnalytics.recent_recoveries?.length > 0 && (
                      <div className="bg-[#1a0527]/50 rounded-lg p-4 border border-[#4A0A66]">
                        <h4 className="text-white text-sm font-medium mb-3 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-[#0FFFE2]" />
                          Recent Recoveries from Deletion Flow
                        </h4>
                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                          {deletionAnalytics.recent_recoveries.slice(0, 5).map((user) => (
                            <div key={user.user_id || user.email} className="flex items-center justify-between p-2 bg-[#2D063E]/30 rounded-lg text-sm">
                              <div>
                                <div className="text-white">{user.name || user.first_name || user.email}</div>
                                <div className="text-[#FDF2F8]/40 text-xs">{user.email}</div>
                              </div>
                              <div className="text-right">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  user.recovered_from_deletion_step === 1 ? 'bg-[#FF7901]/20 text-[#FF7901]' :
                                  user.recovered_from_deletion_step === 2 ? 'bg-orange-500/20 text-orange-400' :
                                  'bg-red-500/20 text-red-400'
                                }`}>
                                  From Warning {user.recovered_from_deletion_step || '?'}
                                </span>
                                {user.recovered_from_deletion_at && (
                                  <div className="text-xs text-[#FDF2F8]/30 mt-1">
                                    {format(new Date(user.recovered_from_deletion_at), 'MMM d, yyyy')}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8 text-[#FDF2F8]/50">
                    <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-[#FDF2F8]/30" />
                    <p className="text-sm">No deletion flow data yet</p>
                    <p className="text-xs text-[#FDF2F8]/30 mt-1">Send deletion warning emails to start tracking</p>
                  </div>
                )}
              </div>

              {/* Email Open Tracking Section */}
              <div className="bg-[#2D063E]/50 rounded-xl p-6 border border-[#4A0A66]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Eye className="w-5 h-5 text-[#0FFFE2]" />
                    Email Open Tracking
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowEmailOpenModal(true);
                      fetchEmailOpenTracking();
                    }}
                    className="border-[#4A0A66] text-[#FDF2F8]/70 hover:bg-[#4A0A66]/30"
                    data-testid="view-email-opens-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Email Opens
                  </Button>
                </div>
                <p className="text-sm text-[#FDF2F8]/60">
                  Track which users opened your re-engagement emails and which ones have not yet opened them.
                </p>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-[#FDF2F8]/50">
              No analytics data available yet
            </div>
          )}
        </div>
      )}

      {/* Email Open Tracking Modal */}
      {showEmailOpenModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
          onClick={() => setShowEmailOpenModal(false)}
          data-testid="email-open-tracking-modal"
        >
          <div 
            className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] rounded-2xl border border-[#4A0A66] w-full max-w-4xl max-h-[85vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-[#4A0A66]">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#0FFFE2]" />
                Email Open Tracking
              </h3>
              <button
                onClick={() => setShowEmailOpenModal(false)}
                className="p-2 rounded-lg hover:bg-[#4A0A66]/50 text-[#FDF2F8]/70 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[calc(85vh-80px)]">
              {emailOpenLoading ? (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#0FFFE2]" />
                  <p className="text-[#FDF2F8]/50">Loading email open data...</p>
                </div>
              ) : emailOpenTracking ? (
                <div className="space-y-6">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-[#4A0A66]/30 p-4 rounded-xl text-center">
                      <Mail className="w-6 h-6 mx-auto mb-2 text-[#FF7901]" />
                      <div className="text-2xl font-bold text-white">{emailOpenTracking.summary?.total_sent || 0}</div>
                      <div className="text-xs text-[#FDF2F8]/50">Emails Sent</div>
                    </div>
                    <div className="bg-[#4A0A66]/30 p-4 rounded-xl text-center">
                      <Eye className="w-6 h-6 mx-auto mb-2 text-[#0FFFE2]" />
                      <div className="text-2xl font-bold text-white">{emailOpenTracking.summary?.total_opened || 0}</div>
                      <div className="text-xs text-[#FDF2F8]/50">Emails Opened</div>
                    </div>
                    <div className="bg-[#4A0A66]/30 p-4 rounded-xl text-center">
                      <TrendingUp className="w-6 h-6 mx-auto mb-2 text-[#df1283]" />
                      <div className="text-2xl font-bold text-white">{emailOpenTracking.summary?.open_rate || 0}%</div>
                      <div className="text-xs text-[#FDF2F8]/50">Open Rate</div>
                    </div>
                  </div>

                  {/* Open Rate by Step */}
                  <div className="bg-[#1a0527] rounded-lg p-4 border border-[#4A0A66]/50">
                    <h4 className="text-white font-medium mb-3">Open Rate by Step</h4>
                    <div className="grid grid-cols-3 gap-3">
                      {[1, 2, 3].map(step => {
                        const stepData = emailOpenTracking.by_step?.[`step${step}`] || {};
                        return (
                          <div key={step} className={`p-3 rounded-lg border ${
                            step === 1 ? 'border-[#0FFFE2]/30 bg-[#0FFFE2]/5' :
                            step === 2 ? 'border-[#FF7901]/30 bg-[#FF7901]/5' :
                            'border-[#df1283]/30 bg-[#df1283]/5'
                          }`}>
                            <div className="text-sm text-[#FDF2F8]/70 mb-1">Step {step}</div>
                            <div className="flex items-baseline gap-2">
                              <span className={`text-xl font-bold ${
                                step === 1 ? 'text-[#0FFFE2]' :
                                step === 2 ? 'text-[#FF7901]' :
                                'text-[#df1283]'
                              }`}>{stepData.open_rate || 0}%</span>
                              <span className="text-xs text-[#FDF2F8]/50">
                                ({stepData.opened || 0}/{stepData.sent || 0})
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Opened Emails List */}
                  <div>
                    <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#0FFFE2]" />
                      Opened Emails ({emailOpenTracking.opened_emails?.length || 0})
                    </h4>
                    {emailOpenTracking.opened_emails?.length > 0 ? (
                      <div className="bg-[#1a0527] rounded-lg border border-[#4A0A66]/50 overflow-hidden max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-[#4A0A66]/30 sticky top-0">
                            <tr>
                              <th className="text-left p-2 text-[#FDF2F8]/70">Recipient</th>
                              <th className="text-left p-2 text-[#FDF2F8]/70">Step</th>
                              <th className="text-left p-2 text-[#FDF2F8]/70">Sent</th>
                              <th className="text-left p-2 text-[#FDF2F8]/70">Opened</th>
                            </tr>
                          </thead>
                          <tbody>
                            {emailOpenTracking.opened_emails.map((email, idx) => (
                              <tr key={email.tracking_id || idx} className="border-t border-[#4A0A66]/30 hover:bg-[#4A0A66]/20">
                                <td className="p-2">
                                  <div className="text-white">{email.recipient_name || 'Unknown'}</div>
                                  <div className="text-xs text-[#FDF2F8]/50">{email.recipient_email}</div>
                                </td>
                                <td className="p-2">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    email.template_type === 'reengagement' ? 'bg-[#0FFFE2]/20 text-[#0FFFE2]' :
                                    email.template_type === 'reengagement_step2' ? 'bg-[#FF7901]/20 text-[#FF7901]' :
                                    'bg-[#df1283]/20 text-[#df1283]'
                                  }`}>
                                    {email.template_type === 'reengagement' ? '1' :
                                     email.template_type === 'reengagement_step2' ? '2' : '3'}
                                  </span>
                                </td>
                                <td className="p-2 text-[#FDF2F8]/50 text-xs">
                                  {email.sent_at && format(new Date(email.sent_at), 'MMM d, HH:mm')}
                                </td>
                                <td className="p-2 text-[#0FFFE2] text-xs">
                                  {email.opened_at && format(new Date(email.opened_at), 'MMM d, HH:mm')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-[#FDF2F8]/50 py-4 text-center bg-[#4A0A66]/20 rounded-lg">
                        No emails opened yet
                      </p>
                    )}
                  </div>

                  {/* Unopened Emails List */}
                  <div>
                    <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                      <EyeOff className="w-4 h-4 text-[#FF7901]" />
                      Unopened Emails ({emailOpenTracking.unopened_emails?.length || 0})
                    </h4>
                    {emailOpenTracking.unopened_emails?.length > 0 ? (
                      <div className="bg-[#1a0527] rounded-lg border border-[#4A0A66]/50 overflow-hidden max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-[#4A0A66]/30 sticky top-0">
                            <tr>
                              <th className="text-left p-2 text-[#FDF2F8]/70">Recipient</th>
                              <th className="text-left p-2 text-[#FDF2F8]/70">Step</th>
                              <th className="text-left p-2 text-[#FDF2F8]/70">Sent</th>
                              <th className="text-left p-2 text-[#FDF2F8]/70">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {emailOpenTracking.unopened_emails.map((email, idx) => (
                              <tr key={email.tracking_id || idx} className="border-t border-[#4A0A66]/30 hover:bg-[#4A0A66]/20">
                                <td className="p-2">
                                  <div className="text-white">{email.recipient_name || 'Unknown'}</div>
                                  <div className="text-xs text-[#FDF2F8]/50">{email.recipient_email}</div>
                                </td>
                                <td className="p-2">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    email.template_type === 'reengagement' ? 'bg-[#0FFFE2]/20 text-[#0FFFE2]' :
                                    email.template_type === 'reengagement_step2' ? 'bg-[#FF7901]/20 text-[#FF7901]' :
                                    'bg-[#df1283]/20 text-[#df1283]'
                                  }`}>
                                    {email.template_type === 'reengagement' ? '1' :
                                     email.template_type === 'reengagement_step2' ? '2' : '3'}
                                  </span>
                                </td>
                                <td className="p-2 text-[#FDF2F8]/50 text-xs">
                                  {email.sent_at && format(new Date(email.sent_at), 'MMM d, HH:mm')}
                                </td>
                                <td className="p-2">
                                  <span className="px-2 py-0.5 bg-[#FF7901]/20 text-[#FF7901] rounded text-xs">
                                    Not opened
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-[#FDF2F8]/50 py-4 text-center bg-[#4A0A66]/20 rounded-lg">
                        All emails have been opened!
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-[#FDF2F8]/50">
                  No email tracking data available yet
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Nudge Tab */}
      {activeTab === 'onboarding-nudge' && (
        <OnboardingNudgeManager />
      )}

      {/* Duplicate Accounts Tab */}
      {activeTab === 'duplicate-accounts' && (
        <DuplicateAccountsManager />
      )}

      {/* Create User Modal */}
      {showCreateUser && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
          onClick={() => {
            setShowCreateUser(false);
            setCreatedUserResult(null);
          }}
        >
          <div 
            className="relative max-w-lg w-full rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{
              background: 'linear-gradient(135deg, rgba(223, 18, 131, 0.1) 0%, rgba(45, 6, 62, 0.95) 30%)',
              border: '1px solid rgba(223, 18, 131, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="create-user-modal"
          >
            {/* Close Button */}
            <button
              onClick={() => {
                setShowCreateUser(false);
                setCreatedUserResult(null);
              }}
              className="absolute top-4 right-4 text-white/50 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#df1283] to-[#FF7901] flex items-center justify-center">
                <UserPlus className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Add New Member</h3>
                <p className="text-sm text-white/60">Create a user account with any tier</p>
              </div>
            </div>

            {/* Success Result */}
            {createdUserResult && (
              <div className="mb-6 p-4 rounded-xl bg-green-500/10 border border-green-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-green-400 font-medium">User Created Successfully!</span>
                </div>
                <div className="space-y-2 text-sm">
                  <p className="text-white/80"><strong>Email:</strong> {createdUserResult.email}</p>
                  <p className="text-white/80"><strong>Name:</strong> {createdUserResult.name}</p>
                  <p className="text-white/80"><strong>Tier:</strong> <TierBadge tier={createdUserResult.membership_tier} /></p>
                  {createdUserResult.temporary_password && (
                    <div className="mt-3 p-3 rounded-lg bg-black/30">
                      <p className="text-white/60 text-xs mb-1">Temporary Password (save this!):</p>
                      <div className="flex items-center gap-2">
                        <code className="text-[#0FFFE2] font-mono">{createdUserResult.temporary_password}</code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(createdUserResult.temporary_password);
                            toast.success('Password copied to clipboard');
                          }}
                          className="text-white/50 hover:text-white"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  {createdUserResult.email_sent && (
                    <p className="text-green-400 text-xs mt-2">
                      <Mail className="w-3 h-3 inline mr-1" />
                      Welcome email sent with login credentials
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleCreateUser} className="space-y-4">
              {/* Name Fields - FIRST */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-white/70 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={newUser.first_name}
                    onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-[#df1283]"
                    placeholder="First name"
                    data-testid="create-user-first-name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/70 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={newUser.last_name}
                    onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-[#df1283]"
                    placeholder="Last name"
                    data-testid="create-user-last-name"
                  />
                </div>
              </div>

              {/* Email - SECOND */}
              <div>
                <label className="block text-sm text-white/70 mb-1">Email Address *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-[#df1283]"
                  placeholder="user@example.com"
                  required
                  data-testid="create-user-email"
                />
              </div>

              {/* Phone Number - THIRD */}
              <div>
                <label className="block text-sm text-white/70 mb-1">Phone Number</label>
                <input
                  type="tel"
                  value={newUser.phone_number || ''}
                  onChange={(e) => setNewUser({...newUser, phone_number: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-[#df1283]"
                  placeholder="+1 (555) 123-4567"
                  data-testid="create-user-phone"
                />
              </div>

              {/* Membership Tier */}
              <div>
                <label className="block text-sm text-white/70 mb-1">Membership Tier</label>
                <select
                  value={newUser.membership_tier}
                  onChange={(e) => setNewUser({...newUser, membership_tier: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white focus:outline-none focus:border-[#df1283]"
                  data-testid="create-user-tier"
                >
                  <option value="free">Free</option>
                  <option value="founding_member">Founding Member</option>
                  <option value="premium">Premium</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              {/* Password (optional) */}
              <div>
                <label className="block text-sm text-white/70 mb-1">Password (optional)</label>
                <input
                  type="text"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-[#df1283]"
                  placeholder="Leave blank to auto-generate"
                  data-testid="create-user-password"
                />
                <p className="text-xs text-white/40 mt-1">If left blank, a random password will be generated</p>
              </div>

              {/* Send Welcome Email */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="send-welcome-email"
                  checked={newUser.send_welcome_email}
                  onChange={(e) => setNewUser({...newUser, send_welcome_email: e.target.checked})}
                  className="w-4 h-4 rounded border-white/20 bg-black/30 text-[#df1283] focus:ring-[#df1283]"
                  data-testid="create-user-send-email"
                />
                <label htmlFor="send-welcome-email" className="text-sm text-white/70">
                  Send welcome email with login credentials
                </label>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => {
                    setShowCreateUser(false);
                    setCreatedUserResult(null);
                  }}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createUserLoading}
                  className="flex-1 bg-gradient-to-r from-[#df1283] to-[#FF7901] text-white hover:opacity-90"
                  data-testid="create-user-submit"
                >
                  {createUserLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </span>
                  ) : (
                    'Create Member'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUser && editingUser && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
          onClick={() => {
            setShowEditUser(false);
            setEditingUser(null);
          }}
        >
          <div 
            className="relative max-w-lg w-full rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{
              background: 'linear-gradient(135deg, rgba(15, 255, 226, 0.1) 0%, rgba(45, 6, 62, 0.95) 30%)',
              border: '1px solid rgba(15, 255, 226, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="edit-user-modal"
          >
            {/* Close Button */}
            <button
              onClick={() => {
                setShowEditUser(false);
                setEditingUser(null);
              }}
              className="absolute top-4 right-4 text-white/50 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#0FFFE2] to-[#00B4D8] flex items-center justify-center">
                <Pencil className="w-6 h-6 text-black" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Edit Member</h3>
                <p className="text-sm text-white/60">{editingUser.email}</p>
              </div>
            </div>

            {/* User Info Summary */}
            <div className="mb-6 p-3 rounded-lg bg-black/20 flex items-center gap-3">
              {editingUser.picture && (
                <img src={editingUser.picture} alt="" className="w-10 h-10 rounded-full" />
              )}
              <div>
                <div className="text-white font-medium">{editingUser.name || editingUser.email}</div>
                <div className="text-xs text-white/50">
                  Created: {editingUser.created_at ? format(new Date(editingUser.created_at), 'MMM d, yyyy') : 'Unknown'}
                </div>
              </div>
              <div className="ml-auto">
                <TierBadge tier={editingUser.membership_tier} />
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleEditUser} className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm text-white/70 mb-1">Email Address</label>
                <input
                  type="email"
                  value={editUserForm.email}
                  onChange={(e) => setEditUserForm({...editUserForm, email: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-[#0FFFE2]"
                  data-testid="edit-user-email"
                />
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-white/70 mb-1">First Name</label>
                  <input
                    type="text"
                    value={editUserForm.first_name}
                    onChange={(e) => setEditUserForm({...editUserForm, first_name: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-[#0FFFE2]"
                    placeholder="First name"
                    data-testid="edit-user-first-name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/70 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={editUserForm.last_name}
                    onChange={(e) => setEditUserForm({...editUserForm, last_name: e.target.value})}
                    className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-[#0FFFE2]"
                    placeholder="Last name"
                    data-testid="edit-user-last-name"
                  />
                </div>
              </div>

              {/* Membership Tier */}
              <div>
                <label className="block text-sm text-white/70 mb-1">Membership Tier</label>
                <select
                  value={editUserForm.membership_tier}
                  onChange={(e) => setEditUserForm({...editUserForm, membership_tier: e.target.value})}
                  className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white focus:outline-none focus:border-[#0FFFE2]"
                  data-testid="edit-user-tier"
                >
                  <option value="free">Free</option>
                  <option value="founding_member">Founding Member</option>
                  <option value="premium">Premium</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              {/* Password Reset Section */}
              <div className="border-t border-white/10 pt-4 mt-4">
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="checkbox"
                    id="reset-password"
                    checked={editUserForm.reset_password}
                    onChange={(e) => setEditUserForm({...editUserForm, reset_password: e.target.checked, new_password: ''})}
                    className="w-4 h-4 rounded border-white/20 bg-black/30 text-[#0FFFE2] focus:ring-[#0FFFE2]"
                    data-testid="edit-user-reset-password"
                  />
                  <label htmlFor="reset-password" className="text-sm text-white/70">
                    Reset Password
                  </label>
                </div>
                
                {editUserForm.reset_password && (
                  <div>
                    <label className="block text-sm text-white/70 mb-1">New Password</label>
                    <input
                      type="text"
                      value={editUserForm.new_password}
                      onChange={(e) => setEditUserForm({...editUserForm, new_password: e.target.value})}
                      className="w-full px-4 py-2.5 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-[#0FFFE2]"
                      placeholder="Enter new password (min 8 characters)"
                      data-testid="edit-user-new-password"
                    />
                  </div>
                )}
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => {
                    setShowEditUser(false);
                    setEditingUser(null);
                  }}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={editUserLoading}
                  className="flex-1 bg-gradient-to-r from-[#0FFFE2] to-[#00B4D8] text-black hover:opacity-90"
                  data-testid="edit-user-submit"
                >
                  {editUserLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </span>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Branded Email Confirmation Dialog */}
      {showEmailConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] rounded-2xl p-6 max-w-md w-full border border-[#df1283]/30 shadow-2xl">
            {/* Header with email icon */}
            <div className="text-center mb-6">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
                emailStepToSend === 1 ? 'bg-[#0FFFE2]/20' :
                emailStepToSend === 2 ? 'bg-[#FF7901]/20' :
                'bg-[#df1283]/20'
              }`}>
                <Mail className={`w-8 h-8 ${
                  emailStepToSend === 1 ? 'text-[#0FFFE2]' :
                  emailStepToSend === 2 ? 'text-[#FF7901]' :
                  'text-[#df1283]'
                }`} />
              </div>
              <h3 className="text-xl font-bold text-white mb-1">
                Send Email {emailStepToSend}
              </h3>
              <p className="text-[#FDF2F8]/60 text-sm">
                {emailStepToSend === 1 ? '"We Miss You" - Initial outreach' :
                 emailStepToSend === 2 ? '"Still Thinking of You" - Gentle follow-up' :
                 '"One Last Note" - Final attempt'}
              </p>
            </div>

            {/* Stats */}
            <div className="bg-[#1a0527] rounded-xl p-4 mb-6 border border-[#4A0A66]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-[#0FFFE2]" />
                  <span className="text-white font-medium">Recipients</span>
                </div>
                <span className="text-2xl font-bold text-[#0FFFE2]">
                  {selectedUsers.length}
                </span>
              </div>
              {selectAllMode && (
                <p className="text-[#FDF2F8]/50 text-xs mt-2 text-center">
                  All inactive users selected across all pages
                </p>
              )}
            </div>

            {/* Email step indicator */}
            <div className="flex justify-center gap-2 mb-6">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    step === emailStepToSend
                      ? step === 1 ? 'bg-[#0FFFE2] text-black scale-110' :
                        step === 2 ? 'bg-[#FF7901] text-black scale-110' :
                        'bg-[#df1283] text-white scale-110'
                      : step < emailStepToSend
                        ? 'bg-[#4A0A66] text-[#FDF2F8]/50'
                        : 'bg-[#1a0527] text-[#FDF2F8]/30 border border-[#4A0A66]'
                  }`}
                >
                  {step}
                </div>
              ))}
            </div>

            {/* Warning note */}
            <p className="text-[#FDF2F8]/50 text-xs text-center mb-6">
              This will send re-engagement emails to {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''}.
              Make sure this is the correct email step for your campaign.
            </p>

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button
                onClick={() => setShowEmailConfirm(false)}
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmSendEmails}
                disabled={loadingAction}
                className={`flex-1 text-black font-semibold ${
                  emailStepToSend === 1 ? 'bg-[#0FFFE2] hover:bg-[#0FFFE2]/80' :
                  emailStepToSend === 2 ? 'bg-[#FF7901] hover:bg-[#FF7901]/80' :
                  'bg-[#df1283] hover:bg-[#df1283]/80 text-white'
                }`}
                data-testid="confirm-send-email-btn"
              >
                {loadingAction ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Send Email {emailStepToSend}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Deletion Warning Confirmation Dialog */}
      {showDeletionConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] rounded-2xl p-6 max-w-md w-full border border-[#FF7901]/30 shadow-2xl">
            {/* Header with warning icon */}
            <div className="text-center mb-6">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
                deletionStepToSend === 1 ? 'bg-[#FF7901]/20' :
                deletionStepToSend === 2 ? 'bg-orange-500/20' :
                'bg-red-500/20'
              }`}>
                <AlertTriangle className={`w-8 h-8 ${
                  deletionStepToSend === 1 ? 'text-[#FF7901]' :
                  deletionStepToSend === 2 ? 'text-orange-400' :
                  'text-red-400'
                }`} />
              </div>
              <h3 className="text-xl font-bold text-white mb-1">
                Deletion Warning {deletionStepToSend}
              </h3>
              <p className="text-[#FDF2F8]/60 text-sm">
                {deletionStepToSend === 1 ? '"Account Scheduled" - Initial notice' :
                 deletionStepToSend === 2 ? '"Time Running Out" - Halfway reminder' :
                 '"FINAL NOTICE" - Last chance'}
              </p>
            </div>

            {/* Stats */}
            <div className="bg-[#1a0527] rounded-xl p-4 mb-6 border border-[#4A0A66]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-[#FF7901]" />
                  <span className="text-white font-medium">Recipients</span>
                </div>
                <span className="text-2xl font-bold text-[#FF7901]">
                  {selectedUsers.length}
                </span>
              </div>
              {selectAllMode && (
                <p className="text-[#FDF2F8]/50 text-xs mt-2 text-center">
                  All inactive users selected across all pages
                </p>
              )}
            </div>

            {/* Deletion step indicator */}
            <div className="flex justify-center gap-2 mb-6">
              {[1, 2, 3].map((step) => (
                <div
                  key={step}
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    step === deletionStepToSend
                      ? step === 1 ? 'bg-[#FF7901] text-black scale-110' :
                        step === 2 ? 'bg-orange-500 text-black scale-110' :
                        'bg-red-500 text-white scale-110'
                      : step < deletionStepToSend
                        ? 'bg-[#4A0A66] text-[#FDF2F8]/50'
                        : 'bg-[#1a0527] text-[#FDF2F8]/30 border border-[#4A0A66]'
                  }`}
                >
                  {step}
                </div>
              ))}
            </div>

            {/* Warning note */}
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-6">
              <p className="text-red-400 text-xs text-center">
                ⚠️ Users who haven't received a deletion notice will be scheduled for deletion.
                Users have {settings.grace_period_days} days to log back in and save their account.
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button
                onClick={() => setShowDeletionConfirm(false)}
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmSendDeletionWarning}
                disabled={loadingAction}
                className={`flex-1 font-semibold ${
                  deletionStepToSend === 1 ? 'bg-[#FF7901] hover:bg-[#FF7901]/80 text-black' :
                  deletionStepToSend === 2 ? 'bg-orange-500 hover:bg-orange-600 text-black' :
                  'bg-red-500 hover:bg-red-600 text-white'
                }`}
                data-testid="confirm-send-deletion-warning-btn"
              >
                {loadingAction ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Send Warning {deletionStepToSend}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InactiveAccountsTab;
